import { Routes, Route, Navigate } from "react-router-dom";
import CompanyProfile from "../pages/client/Profile";
import MyProjects from "../pages/client/MyProjects";
import MyMatchedTalents from "../pages/client/MyMatchedTalents";
import { CLIENT_TALENTS_ENABLED } from "../utils/featureFlags";
import Onboarding from "../pages/client/Onboarding";
import ProjectCreate from "../pages/project/ProjectCreate";
import ProjectDetails from "../pages/project/ProjectDetails";
import ProjectEdit from "../pages/project/ProjectEdit";
import Settings from "../pages/settings/Settings";
import ClientOnboardingCheck from "../components/client/ClientOnboardingCheck";
import ProjectEditProtection from "../components/project/ProjectEditProtection";
import { useClient } from "../hooks/useClient";
import { useMyProjects } from "../hooks/useProject";
import { CircularProgress, Box } from "@mui/material";

/**
 * Routes for client user type
 * Includes logic for handling onboarding flow and project management
 */
const ClientRoutes: React.FC = () => {
	const { data: client, isLoading } = useClient();

	// Onboarding status drives BOTH whether protected routes are accessible and
	// the default landing page. We only fetch projects once we know onboarding
	// is completed — projects are gated by it anyway, no point in firing the
	// request before that.
	const onboardingCompleted = !!client?.onboardingCompleted;

	// One-page probe (pageSize=1) — we only need to know whether the client has
	// ≥1 project, not the list itself. Lets us route brand-new clients to the
	// matched-talents page instead of the empty "My projects" screen.
	// Gated on `onboardingCompleted` so we don't fire the query for clients
	// still in the onboarding wizard (the API would reject it anyway).
	const { data: projectsProbe, isLoading: isLoadingProjects } = useMyProjects(
		1,
		1,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		onboardingCompleted
	);

	if (isLoading || (onboardingCompleted && isLoadingProjects)) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" height="400px">
				<CircularProgress />
			</Box>
		);
	}

	const hasProjects = (projectsProbe?.totalCount ?? 0) > 0;

	return (
		<Routes>
			{/* Onboarding route is always accessible */}
			<Route path="/profile/onboarding" element={<Onboarding />} />

			{/* Profile route is always accessible */}
			<Route path="/profile" element={<CompanyProfile />} />

			{/* Protected routes - only accessible after onboarding */}
			<Route
				path="/projects"
				element={
					<ClientOnboardingCheck>
						<MyProjects />
					</ClientOnboardingCheck>
				}
			/>

			<Route
				path="/projects/create"
				element={
					<ClientOnboardingCheck>
						<ProjectCreate />
					</ClientOnboardingCheck>
				}
			/>

			<Route
				path="/projects/:id"
				element={
					<ClientOnboardingCheck>
						<ProjectDetails />
					</ClientOnboardingCheck>
				}
			/>

			<Route
				path="/projects/:id/edit"
				element={
					<ClientOnboardingCheck>
						<ProjectEditProtection>
							<ProjectEdit />
						</ProjectEditProtection>
					</ClientOnboardingCheck>
				}
			/>

			{/* Suggested anonymized talents (Option A) */}
			<Route
				path="/talents"
				element={
					CLIENT_TALENTS_ENABLED ? (
						<ClientOnboardingCheck>
							<MyMatchedTalents />
						</ClientOnboardingCheck>
					) : (
						<Navigate to="/projects" replace />
					)
				}
			/>

			{/* Settings route - always accessible */}
			<Route path="/settings" element={<Settings />} />

			{/* Default redirect:
			    - onboarding not done → finish the onboarding wizard
			    - onboarding done + already has projects → projects list (their
			      operational home)
			    - onboarding done + zero projects → matched talents page so
			      brand-new clients land on something actionable instead of an
			      empty projects list (Option A entry point) */}
			<Route
				path="*"
				element={
					onboardingCompleted ? (
						hasProjects ? (
							<Navigate to="/projects" />
						) : (
							<Navigate to={CLIENT_TALENTS_ENABLED ? "/talents" : "/projects"} />
						)
					) : (
						<Navigate to="/profile/onboarding" />
					)
				}
			/>
		</Routes>
	);
};

export default ClientRoutes;
